import { createSlice } from "@reduxjs/toolkit";
import {
  ApiData,
  ApiResponse,
  AppKeyData,
  MethodData,
} from "../../../types/apis";
import {
  executeRequestAction,
  getApisDetails,
  getMethodsByAPI,
  getUserApisDetails,
  getUserAppKeys,
  getUserMethodsByAPI,
} from "../../thunks/apis/api.thunks";
import { ApiLoadingStatusEnum } from "../../../shared/constants/enums/Apis";

export interface IAppState {
  apiError: boolean;
  apiErrorCode: string;
  apis: ApiData[];
  appKeys: AppKeyData[];
  methods: MethodData[];
  requestResponse: ApiResponse | null;
  isSidebarOpen: boolean;
  loadingApi: boolean;
  loadingApiByUser: boolean;
  loadingMethod: boolean;
  loadingMethodByUser: boolean;
  loadingRequest: boolean;
  apiLoadingStatus: ApiLoadingStatusEnum;
  apiByUserLoadingStatus: ApiLoadingStatusEnum;
  requestLoadingStatus: ApiLoadingStatusEnum;
  appKeyLoadingStatus: ApiLoadingStatusEnum;
  methodLoadingStatus: ApiLoadingStatusEnum;
  methodByUserLoadingStatus: ApiLoadingStatusEnum;
}

const initialState: IAppState = {
  apiError: false,
  apiErrorCode: "",
  apis: [],
  appKeys: [],
  methods: [],
  requestResponse: null,
  isSidebarOpen: true,
  loadingApi: false,
  loadingApiByUser: false,
  loadingMethod: false,
  loadingMethodByUser: false,
  loadingRequest: false,
  apiLoadingStatus: ApiLoadingStatusEnum.IDLE,
  apiByUserLoadingStatus: ApiLoadingStatusEnum.IDLE,
  appKeyLoadingStatus: ApiLoadingStatusEnum.IDLE,
  methodLoadingStatus: ApiLoadingStatusEnum.IDLE,
  methodByUserLoadingStatus: ApiLoadingStatusEnum.IDLE,
  requestLoadingStatus: ApiLoadingStatusEnum.IDLE,
};

export const apiDataSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getUserApisDetails.pending, (state) => {
        state.loadingApiByUser = true;
        state.apiByUserLoadingStatus = ApiLoadingStatusEnum.PENDING;
      })
      .addCase(getUserApisDetails.fulfilled, (state, { payload }) => {
        state.loadingApiByUser = false;
        state.apiByUserLoadingStatus = ApiLoadingStatusEnum.SUCCESS;
        state.apis = payload.api;
      })
      .addCase(getUserApisDetails.rejected, (state) => {
        state.loadingApiByUser = false;
        state.apiByUserLoadingStatus = ApiLoadingStatusEnum.FAILED;
      })
      .addCase(getApisDetails.pending, (state) => {
        state.loadingApi = true;
        state.apiLoadingStatus = ApiLoadingStatusEnum.PENDING;
      })
      .addCase(getApisDetails.fulfilled, (state, { payload }) => {
        state.loadingApi = false;
        state.apiLoadingStatus = ApiLoadingStatusEnum.SUCCESS;
        state.apis = payload.api;
      })
      .addCase(getApisDetails.rejected, (state) => {
        state.loadingApi = false;
        state.apiLoadingStatus = ApiLoadingStatusEnum.FAILED;
      })
      .addCase(getUserMethodsByAPI.pending, (state) => {
        state.loadingMethodByUser = true;
        state.methodByUserLoadingStatus = ApiLoadingStatusEnum.PENDING;
      })
      .addCase(getUserMethodsByAPI.fulfilled, (state, { payload }) => {
        state.loadingMethodByUser = false;
        state.methodByUserLoadingStatus = ApiLoadingStatusEnum.SUCCESS;
        state.methods = payload.method || [];
      })
      .addCase(getUserMethodsByAPI.rejected, (state) => {
        state.loadingMethodByUser = false;
        state.methodByUserLoadingStatus = ApiLoadingStatusEnum.FAILED;
      })
      .addCase(getMethodsByAPI.pending, (state) => {
        state.loadingMethod = true;
        state.methodLoadingStatus = ApiLoadingStatusEnum.PENDING;
      })
      .addCase(getMethodsByAPI.fulfilled, (state, { payload }) => {
        state.loadingMethod = false;
        state.methodLoadingStatus = ApiLoadingStatusEnum.SUCCESS;
        state.methods = payload.method || [];
      })
      .addCase(getMethodsByAPI.rejected, (state) => {
        state.loadingMethod = false;
        state.methodLoadingStatus = ApiLoadingStatusEnum.FAILED;
      })
      .addCase(getUserAppKeys.pending, (state) => {
        state.loadingMethod = true;
        state.appKeyLoadingStatus = ApiLoadingStatusEnum.PENDING;
      })
      .addCase(getUserAppKeys.fulfilled, (state, { payload }) => {
        state.loadingMethod = false;
        state.appKeyLoadingStatus = ApiLoadingStatusEnum.SUCCESS;
        state.appKeys = payload.app_key;
      })
      .addCase(getUserAppKeys.rejected, (state) => {
        state.loadingMethod = false;
        state.appKeyLoadingStatus = ApiLoadingStatusEnum.FAILED;
      })
      .addCase(executeRequestAction.pending, (state) => {
        state.loadingRequest = true;
        state.requestLoadingStatus = ApiLoadingStatusEnum.PENDING;
        state.requestResponse = null;
      })
      .addCase(executeRequestAction.fulfilled, (state, { payload }) => {
        state.loadingRequest = false;
        state.requestLoadingStatus = ApiLoadingStatusEnum.SUCCESS;
        state.requestResponse = payload;
      })
      .addCase(executeRequestAction.rejected, (state, action) => {
        state.loadingRequest = false;
        state.requestLoadingStatus = ApiLoadingStatusEnum.FAILED;

        const payload = (action.payload ?? {}) as {
          message?: string;
          code?: number;
          error?: string;
          requestHeaders?: Record<string, string>;
          responseHeaders?: Record<string, string>;
          timing?: number;
          url?: string;
          timestamp?: string;
        };

        state.requestResponse = {
          success: false,
          status: payload.code || 500,
          statusText: payload.message || "Request failed",
          data: null,
          requestHeaders: payload.requestHeaders || {},
          responseHeaders: payload.responseHeaders || {},
          timing: payload.timing || 0,
          error: payload.error || action.error?.name,
          message: payload.message || action.error?.message || "Request failed",
          code: payload.code || 500,
          url: payload.url,
          timestamp: payload.timestamp,
        };
      });
  },
  initialState,
  name: "api",
  reducers: {
    setIsSidebarOpen(state, action: { payload: boolean }) {
      state.isSidebarOpen = action.payload;
    },
    setIsLoadingApi(state, action: { payload: boolean }) {
      state.loadingApi = action.payload;
    },
    setIsLoadingApiByUser(state, action: { payload: boolean }) {
      state.loadingApiByUser = action.payload;
    },
    setIsLoadingMethod(state, action: { payload: boolean }) {
      state.loadingMethod = action.payload;
    },
    setIsLoadingMethodByUser(state, action: { payload: boolean }) {
      state.loadingMethodByUser = action.payload;
    },
    removeError(state) {
      state.apiError = false;
    },
    resetAppErrorCode(state) {
      state.apiErrorCode = "";
    },
  },
});

export default apiDataSlice.reducer;
